import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard, FolderKanban, ListTodo, TrendingUp, Wallet, FileBarChart2, Users, LogOut, Receipt,
  Landmark, BadgeIndianRupee, ShieldCheck, Layers, HandCoins, PiggyBank, FileCheck2, Banknote,
  Grid3x3, Clock, Scale, Database, Inbox, ClipboardList, Timer, Activity, AlertOctagon, AlertTriangle,
  LifeBuoy, Menu,
} from "lucide-react";
import { roleLabel } from "@/lib/roles";
import { canAccess } from "@/lib/access";
import { RequireAccess } from "@/components/RequireAccess";
import AiChat from "@/components/AiChat";
import InstallPrompt from "@/components/InstallPrompt";
import NotificationBell from "@/components/NotificationBell";

const nav = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard", end: true },
  { to: "/app/approvals", label: "Pending on Me", icon: Inbox, testId: "nav-approvals", rolesAllowed: ["Admin", "Approver", "Authorizer", "ProjectManager", "Accounts"] },
  { to: "/app/my-requests", label: "My Requests", icon: ClipboardList, testId: "nav-my-requests" },
  { to: "/app/projects", label: "Projects", icon: FolderKanban, testId: "nav-projects" },
  { to: "/app/monitoring", label: "Project Monitoring", icon: TrendingUp, testId: "nav-monitoring" },
  { to: "/app/tasks", label: "My Tasks", icon: ListTodo, testId: "nav-tasks" },
  { section: "Money" },
  { to: "/app/budget-v2", label: "Budgets", icon: Landmark, testId: "nav-budget-v2" },
  { to: "/app/expenses-v2", label: "Project Expenses", icon: Landmark, testId: "nav-expenses-v2" },
  { to: "/app/budget-reports", label: "Budget Reports", icon: Landmark, testId: "nav-budget-reports" },
  { to: "/app/reimbursements", label: "Reimbursement Claims", icon: Receipt, testId: "nav-reimbursements" },
  { to: "/app/advances", label: "Advances", icon: HandCoins, testId: "nav-advances" },
  { to: "/app/fund-receipts", label: "Fund Receipts", icon: PiggyBank, testId: "nav-fund-receipts", rolesAllowed: ["Admin", "Accounts", "Approver", "Authorizer", "Auditor", "ProjectManager"] },
  { to: "/app/salary", label: "Salary Import", icon: BadgeIndianRupee, testId: "nav-salary", rolesAllowed: ["Admin", "Accounts"] },
  { to: "/app/batch-pay", label: "Batch Pay", icon: Banknote, testId: "nav-batch-pay", rolesAllowed: ["Admin", "Accounts"] },
  // Legacy Finance/Expenses module fully retired 2026-02-11 — /app/finance now redirects to /app/expenses-v2 with a toast.
  { section: "Reports" },
  { to: "/app/reports", label: "Reports", icon: FileBarChart2, testId: "nav-reports", end: true },
  { to: "/app/monthly-grid", label: "12-Month Grid", icon: Grid3x3, testId: "nav-monthly-grid" },
  { to: "/app/ageing", label: "Ageing", icon: Clock, testId: "nav-ageing" },
  { to: "/app/cash-statement", label: "Cash Statement", icon: Scale, testId: "nav-cash-statement" },
  { to: "/app/utilisation-certificate", label: "Utilisation Certificate", icon: FileCheck2, testId: "nav-uc-v2", rolesAllowed: ["Admin", "Accounts"] },
  { to: "/app/reports/rejected-review", label: "Rejected Review", icon: AlertTriangle, testId: "nav-rejected-review", rolesAllowed: ["Admin"] },
  { to: "/app/reports/self-disbursed", label: "Self-disbursed", icon: Scale, testId: "nav-self-disbursed", rolesAllowed: ["Admin"] },
  { section: "Administration" },
  { to: "/app/masters", label: "Masters", icon: Layers, testId: "nav-masters", rolesAllowed: ["Admin", "Accounts", "ProjectManager", "Auditor"] },
  { to: "/app/employees", label: "Employees", icon: Users, testId: "nav-employees" },
  { to: "/app/users", label: "Users & Roles", icon: ShieldCheck, testId: "nav-users", rolesAllowed: ["Admin"] },
  { to: "/app/migration", label: "Migration", icon: Database, testId: "nav-migration", rolesAllowed: ["Admin", "Accounts"] },
  { to: "/app/budget-admin", label: "Budget Admin Tools", icon: Database, testId: "nav-budget-admin", rolesAllowed: ["Admin"] },
  { to: "/app/uc-templates", label: "UC Templates", icon: FileCheck2, testId: "nav-uc-templates", rolesAllowed: ["Admin"] },
  { to: "/app/needs-review", label: "Needs Review", icon: AlertTriangle, testId: "nav-needs-review", rolesAllowed: ["Admin"], badgeKey: "needsReview" },
  { to: "/app/settings/sla", label: "SLA Settings", icon: Timer, testId: "nav-sla", rolesAllowed: ["Admin"] },
  { to: "/app/cron", label: "Cron Runs", icon: Activity, testId: "nav-cron", rolesAllowed: ["Admin", "Auditor"] },
];

/** Full sidebar content — shared between desktop (fixed) and mobile (Sheet drawer). */
function SidebarBody({ user, overdue, badges = {}, onLogout, onNavigate }) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col h-full bg-white">
      <div className="p-5 border-b border-slate-200 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-white ring-1 ring-slate-200 overflow-hidden flex items-center justify-center shrink-0">
          <img src="/rkf-logo.webp" alt="RKF" className="w-full h-full object-contain" data-testid="app-logo" />
        </div>
        <div>
          <div className="font-display font-bold text-sm leading-tight flex items-center gap-2">
            My RKF
            <NotificationBell />
            {overdue > 0 && (
              <span
                className="inline-flex items-center gap-1 bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full animate-pulse cursor-pointer"
                onClick={() => { navigate("/app/approvals"); onNavigate?.(); }}
                title={`${overdue} item${overdue !== 1 ? "s" : ""} past SLA`}
                data-testid="overdue-badge"
              >
                <AlertOctagon className="w-2.5 h-2.5" /> {overdue}
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-500 uppercase tracking-wider">Governance · v1.3</div>
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {nav.map((n, i) => {
          if (n.section) {
            const next = nav.slice(i + 1);
            const end = next.findIndex(x => x.section);
            const visible = (end === -1 ? next : next.slice(0, end)).some(x => canAccess(user, x.to));
            if (!visible) return null;
            return <div key={i} className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-widest text-slate-400 font-semibold" data-testid={`nav-section-${n.section.toLowerCase()}`}>{n.section}</div>;
          }
          if (user && !canAccess(user, n.to)) return null;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={onNavigate}
              data-testid={n.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              <span className="flex-1">{n.label}</span>
              {n.badgeKey && badges[n.badgeKey] > 0 && (
                <span
                  className="ml-auto bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center"
                  data-testid={`badge-${n.badgeKey}`}
                >
                  {badges[n.badgeKey]}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-3 border-t border-slate-200">
        <NavLink
          to="/app/help"
          onClick={onNavigate}
          data-testid="nav-help"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
            }`
          }
        >
          <LifeBuoy className="w-4 h-4" />
          Help &amp; Guide
        </NavLink>
        <NavLink to="/app/profile" onClick={onNavigate} className="block px-3 py-2 mt-2 mb-1 rounded-md hover:bg-slate-100" data-testid="nav-profile">
          <div className="text-sm font-semibold truncate" data-testid="current-user-name">{user?.name}</div>
          <div className="text-xs text-slate-500">{roleLabel(user?.role)} · My Profile</div>
        </NavLink>
        <Button variant="ghost" size="sm" className="w-full justify-start text-slate-600" onClick={onLogout} data-testid="logout-btn">
          <LogOut className="w-4 h-4 mr-2" /> Sign out
        </Button>
      </div>
    </div>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [overdue, setOverdue] = useState(0);
  const [needsReview, setNeedsReview] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  const doLogout = () => { setMobileOpen(false); logout(); navigate("/login"); };

  useEffect(() => {
    if (!user || !["Admin","Approver","Authorizer","ProjectManager","Accounts"].includes(user.role)) return;
    let stop = false;
    const load = async () => {
      try {
        const { data } = await (await import("@/lib/api")).api.get("/approvals/my-overdue");
        if (!stop) setOverdue(data.count || 0);
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { stop = true; clearInterval(t); };
  }, [user]);

  useEffect(() => {
    if (user?.role !== "Admin") return;
    let stop = false;
    const load = async () => {
      try {
        const { data } = await (await import("@/lib/api")).api.get("/needs-review/legacy-budget?status=open");
        if (!stop) setNeedsReview(data.counts?.open || 0);
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 120000);
    return () => { stop = true; clearInterval(t); };
  }, [user?.role]);

  // Close the mobile drawer whenever the route changes (safety — NavLink onClick already does this).
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Desktop sidebar — hidden below lg */}
      <aside className="hidden lg:flex w-64 bg-white border-r border-slate-200 flex-col shrink-0 max-h-screen sticky top-0" data-testid="desktop-sidebar">
        <SidebarBody user={user} overdue={overdue} badges={{ needsReview }} onLogout={doLogout} />
      </aside>

      {/* Main column — includes mobile top bar */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar — visible below lg */}
        <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-slate-200 flex items-center justify-between px-4 py-3" data-testid="mobile-topbar">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="w-10 h-10 rounded-md hover:bg-slate-100 flex items-center justify-center"
                aria-label="Open menu"
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5 text-slate-700" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72 max-w-[85vw]" data-testid="mobile-sidebar">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <SidebarBody
                user={user}
                overdue={overdue}
                badges={{ needsReview }}
                onLogout={doLogout}
                onNavigate={() => setMobileOpen(false)}
              />
            </SheetContent>
          </Sheet>

          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-md bg-white ring-1 ring-slate-200 overflow-hidden flex items-center justify-center shrink-0">
              <img src="/rkf-logo.webp" alt="RKF" className="w-full h-full object-contain" />
            </div>
            <div className="font-display font-bold text-sm truncate">My RKF</div>
            {overdue > 0 && (
              <span
                className="inline-flex items-center gap-1 bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full animate-pulse cursor-pointer shrink-0"
                onClick={() => navigate("/app/approvals")}
                data-testid="overdue-badge-mobile"
              >
                <AlertOctagon className="w-2.5 h-2.5" /> {overdue}
              </span>
            )}
          </div>

          <button
            type="button"
            onClick={doLogout}
            className="w-10 h-10 rounded-md hover:bg-slate-100 flex items-center justify-center"
            aria-label="Sign out"
            data-testid="mobile-logout-btn"
          >
            <LogOut className="w-5 h-5 text-slate-700" />
          </button>
        </header>

        <main className="flex-1 overflow-x-hidden">
          <RequireAccess><Outlet /></RequireAccess>
        </main>
      </div>

      <AiChat />
      <InstallPrompt />
    </div>
  );
}
